import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import styles from './equipments.module.css';
import { useHasPermission } from '../../contexts/auth';

export const Equipments = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const canViewEquipment = useHasPermission("settlement.view_equipment");
  const canViewEquipmentProducer = useHasPermission("settlement.view_equipmentproducer");
  const canViewEquipmentAttribute = useHasPermission("settlement.view_equipmentattribute");

  const pathname = location.pathname;
  const basePath = "/equipment-section";

  const visibleTabs = [
    ...(canViewEquipment ? [{ key: 'equipments', path: `${basePath}/equipments` }] : []),
    ...(canViewEquipmentProducer ? [{ key: 'producers', path: `${basePath}/producers` }] : []),
    ...(canViewEquipmentAttribute
      ? [{ key: 'configurations', path: `${basePath}/configurations` }]
      : []),
  ];

  if (visibleTabs.length === 0) {
    return null;
  }

  // Equipment detail pages (/equipment-section/equipments/:id/...) render their
  // own dedicated tab bar (see ViewEquipment), so the top-level section tabs
  // are hidden there to avoid stacking two tab bars. Every other route in this
  // section - including the configurations list/detail and producer detail,
  // which don't have their own tabs - should keep the section tabs visible.
  const isEquipmentDetail = pathname.startsWith(`${basePath}/equipments/`);
  const showTopLevelTabs = !isEquipmentDetail;

  const getTabValue = () => {
    if (pathname.includes('/producers')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'producers');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/configurations')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'configurations');
      return index >= 0 ? index : 0;
    }
    // equipments tab: /equipment-section/equipments or /equipment-section/equipments/:id (equipment detail)
    const index = visibleTabs.findIndex(tab => tab.key === 'equipments');
    return index >= 0 ? index : 0;
  };

  return (
    <Box className={styles.container}>
      {showTopLevelTabs && (
        <Tabs
          value={getTabValue()}
          variant="scrollable"
          scrollButtons="auto"
        >
          {visibleTabs.map((tab) => {
            const labels: Record<string, string> = {
              equipments: t("Equipments"),
              producers: t("Producers"),
              configurations: t("Configurations"),
            };
            return (
              <Tab
                key={tab.key}
                label={labels[tab.key]}
                onClick={() => navigate(tab.path)}
              />
            );
          })}
        </Tabs>
      )}
      <Box className={styles.content}>
        <Outlet />
      </Box>
    </Box>
  );
};
